import { Drawer, DrawerOverlay, DrawerContent, DrawerBody, List, ListItem, Button, useDisclosure } from '@chakra-ui/react'
import { FC, useMemo, useState } from "react";
import { v4 as uuid } from 'uuid'
import { switchAssetMode } from "../api";
import { getTypeofAsset } from "../utils";
import { getAssetByPath, Layout } from "domains/drag-and-drop";
import { usePackContext } from "domains/pack";
import { DeleteAlertDialog } from "components";
import { usePack, useRunSync } from "lib/hooks";
import { Asset, AssetTypes, DisplayTypes } from "lib/types";
import { isImageEmbeddable } from "lib/utils";
import { openFile } from "../base/file/utils";
import { openLink } from "../base/link/utils";
import { EditFileModal } from "../base/file/modals";
import { EditTextModal } from "../base/text/modals";
import { EditHtmlModal } from "../base/html/modals";
import { EditLinkModal } from "../base/link/modals";

type Props = {
  selectedAssets: any[];
  onClose: () => void;
  isOpen: boolean;
  layout: Layout;
}

enum Actions {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  OPEN = 'OPEN',
  EMBEDDABLE = 'EMBEDDABLE',
}

type AllowedActions = (keyof typeof Actions)[];
type ActionButtonVariant = "destructive" | "light" | "primary";

const FileActions: AllowedActions = [ Actions.OPEN, Actions.EDIT, Actions.EMBEDDABLE, Actions.DELETE ];
const TextActions: AllowedActions = [ Actions.EDIT, Actions.DELETE ];
const HTMLActions: AllowedActions = [ Actions.EDIT, Actions.DELETE ];
const LinkActions: AllowedActions = [ Actions.OPEN, Actions.EDIT, Actions.EMBEDDABLE, Actions.DELETE ];

const AssetActions: { [ V in AssetTypes ]: AllowedActions } = {
  [AssetTypes.FILE]: FileActions,
  [AssetTypes.TEXT]: TextActions,
  [AssetTypes.HTML]: HTMLActions,
  [AssetTypes.LINK]: LinkActions,
  [AssetTypes.PROMPT]: null
}

const editModalComponents = {
  [AssetTypes.FILE]: EditFileModal,
  [AssetTypes.TEXT]: EditTextModal,
  [AssetTypes.HTML]: EditHtmlModal,
  [AssetTypes.LINK]: EditLinkModal,
  'unknown': () => null,
}

const onOpen = (e, pack, asset: Asset<any>) => {
  e.stopPropagation();
  asset.type === AssetTypes.FILE ? openFile(e, pack, asset) : openLink(e, asset);
}

const getButtonVariant = (action: keyof typeof Actions): ActionButtonVariant => {
  if (action === Actions.DELETE) {
    return "destructive";
  }

  if (action === Actions.OPEN) {
    return "primary";
  }

  return "light";
}

export const AssetActionsDrawer: FC<Props> = ({ selectedAssets, layout, onClose, isOpen }) => {
  const { packId } = usePackContext();
  const { data: pack } = usePack(packId);
  const [targetAsset, setTargetAsset] = useState({ type: 'unknown' });
  const editModal = useDisclosure();
  const deleteDialog = useDisclosure();
  const { sync } = useRunSync(packId);

  const openEditModal = (e, asset) => {
    e.preventDefault();
    setTargetAsset(asset);
    editModal.onOpen();
  }

  const openDeleteModal = (e, asset) => {
    e.preventDefault();
    setTargetAsset(asset);
    deleteDialog.onOpen();
  }

  const toggleAssetMode = async (asset) => {
    try {
      await switchAssetMode(asset, pack.key);
      await sync();
    } catch (e) {
      // TODO: Error Notification
    }
  }

  const DrawerActions: { [V in Actions ]: (e: MouseEvent, pack: any, asset: Asset<any>) => void } = {
    [Actions.OPEN]: (e, pack, asset) => onOpen(e, pack, asset),
    [Actions.EDIT]: openEditModal,
    [Actions.DELETE]: openDeleteModal,
    [Actions.EMBEDDABLE]: async (_, asset) => await toggleAssetMode(asset),
  }

  const asset = useMemo(() => {
    const isSingleSelected = selectedAssets.length === 1;

    if (!isSingleSelected) {
      return null;
    }

    let path = selectedAssets[0];

    if (path.length === 1) {
      path = [path]
    } else {
      path = path.split('-')
    }

    return getAssetByPath(layout, path);
  }, [layout, selectedAssets]);

  const isAbleToBeEmbedded = useMemo(() => {
    if (!asset) {
      return false;
    }

    if (asset.type === AssetTypes.LINK) {
      return true;
    }

    const displayType = getTypeofAsset(asset as any);

    if (displayType === DisplayTypes.IMAGE) {
      return isImageEmbeddable(asset.data.content_type);
    }

    return asset.data?.content_type === 'video/mp4' || displayType === DisplayTypes.OEMBED;
  }, [asset]);

  const allowedActions = useMemo(() => {
    if (!asset?.type) {
      return [];
    }

    const actions = AssetActions[asset.type];

    if (!actions) {
      return [];
    }

    if (actions.includes(Actions.EMBEDDABLE) && !isAbleToBeEmbedded) {
      return actions.filter((action) => action !== Actions.EMBEDDABLE);
    }

    return actions;
  }, [asset, isAbleToBeEmbedded]);

  const getListTitle = (action: keyof typeof Actions): string => {
    if (action === Actions.EMBEDDABLE) {
      return asset.data?.embedded ? "UNEMBED" : "EMBED"
    }

    return action;
  }

 const EditModal = editModalComponents[targetAsset?.type];

  return (
    <>
      <Drawer onClose={onClose} isOpen={isOpen} placement={"bottom"}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerBody pt={"20px"} pb={"20px"}>
            <List spacing={3}>
              {allowedActions.map((action) => (
                <ListItem key={uuid()}>
                  <Button
                    size="md"
                    onClick={(e) => {
                      onClose();
                      DrawerActions[action](e, pack, asset)
                    }}
                    width={"100%"}
                    variant={getButtonVariant(action)}
                  >
                    { getListTitle(action) }
                  </Button>
                </ListItem>
              ))}
            </List>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <EditModal
        asset={targetAsset}
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
      />
      <DeleteAlertDialog
        title={"Are you sure you want to delete this asset?"}
        content={"This action cannot be undone."}
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        asset={targetAsset}
      />
    </>
  )
}
